import { FC, useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';

import {
    BackLink,
    Headline,
    Layout,
    Page,
    Stack,
    Tab,
    TabGroup,
} from '@servicetitan/design-system';
import {
    ApiApplicationAccessProvider,
    APPS_ROUTING,
    IAppsRouting,
    OWNER_APPS_API_TOKEN,
} from '@servicetitan/public-api-app-mgmt';
import { provide, Provider, useDependencies } from '@servicetitan/react-ioc';

import { PageSpinner } from '../../common';
import { PartnerCredentialsTerms } from '../../terms';
import { API_GROUP_TOKEN, OwnerAppsApi, OWNER_TOKEN } from '../api/owner-apps-api';
import { ClientsPageStore } from '../stores/clients-page.store';
import { ResourceOwnerInfo } from './resource-owner-info';

export interface ClientsPageProps {
    apiGroupName: string;
    owner: string;
    appId?: string;
}

export const ClientsPage: FC<ClientsPageProps> = provide({
    singletons: [ClientsPageStore],
})(
    observer(({ apiGroupName, owner, appId }) => {
        const [store] = useDependencies(ClientsPageStore);
        const history = useHistory();

        useEffect(() => {
            store.load(apiGroupName, owner, appId);
        }, [store, apiGroupName, owner, appId]);

        const appsRouting: IAppsRouting = useMemo(
            () => ({
                goToAppList: () => {
                    history.push(`/clients/${apiGroupName}/${owner}`);
                },
                goToAppDetails: (appId: string) => {
                    history.push(`/clients/${apiGroupName}/${owner}/${appId}`);
                },
            }),
            [history, apiGroupName, owner],
        );

        const goToHomePage = useCallback(() => {
            history.push('/');
        }, [history]);

        if (store.loading) {
            return <PageSpinner />;
        }

        return (
            <Page
                header={
                    <Stack direction="column">
                        <Stack.Item>
                            <BackLink pageLabel="Home" onClick={goToHomePage} />
                        </Stack.Item>
                        <Stack.Item>
                            <Headline size="large">Manage Integrations and API Access</Headline>
                        </Stack.Item>
                    </Stack>
                }
            >
                <Layout>
                    {store.apiGroups.length > 1 && (
                        <TabGroup>
                            {store.apiGroups.map(apiGroup => (
                                <Tab
                                    key={apiGroup.name}
                                    active={apiGroup === store.selectedApiGroup}
                                    onClick={() => {
                                        history.push(
                                            `/clients/${apiGroup.name}/${apiGroup.assignedResourceOwners[0]}`,
                                        );
                                    }}
                                >
                                    {apiGroup.displayName}
                                </Tab>
                            ))}
                        </TabGroup>
                    )}
                    {store.selectedApiGroup && store.selectedResourceOwner && (
                        <Provider
                            key={`${store.selectedApiGroup.name} ${store.selectedResourceOwner}`}
                            singletons={[
                                { provide: APPS_ROUTING, useValue: appsRouting },
                                {
                                    provide: API_GROUP_TOKEN,
                                    useValue: store.selectedApiGroup,
                                },
                                { provide: OWNER_TOKEN, useValue: store.selectedResourceOwner },
                                { provide: OWNER_APPS_API_TOKEN, useClass: OwnerAppsApi },
                            ]}
                        >
                            <ApiApplicationAccessProvider
                                resourceOwnerHeaderContent={
                                    <ResourceOwnerInfo
                                        selectedApiGroup={store.selectedApiGroup}
                                        selectedResourceOwner={store.selectedResourceOwner}
                                    />
                                }
                                termsContent={PartnerCredentialsTerms}
                                appId={store.appId}
                            />
                        </Provider>
                    )}
                </Layout>
            </Page>
        );
    }),
);
ClientsPage.displayName = 'ClientsPage';
